import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import DashboardModule from './DashboardModule';
import { desktopTheme as t } from '../../styles/desktopTheme';
import { 
  toggleEditMode, 
  addModule, 
  setNewModuleDropdownOpen,
  ensureDefaultModules,
  resetLayout,
  MODULE_TYPES 
} from '../../store/dashboard/dashboardSlice';

const DashboardContainer = styled.div`
  width: 100%;
  max-width: 1400px;
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const EditToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${t.textSecondary};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MetaRow = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: ${t.textTertiary};
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid ${t.borderSubtle};
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderStrong};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${t.accent};
  border: none;
  border-radius: 6px;
  color: ${t.textPrimary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${t.accentHover};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const NewModuleDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: ${t.panel};
  border: 1px solid ${t.borderSubtle};
  border-radius: 8px;
  padding: 8px 0;
  min-width: 160px;
  z-index: 100;
  box-shadow: ${t.shadowMenu};
`;

const DropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${t.textPrimary};
  }
`;

const DropdownArrow = styled.span`
  font-size: 10px;
  margin-left: 4px;
`;

const ModulesGrid = styled.div`
  display: grid;
  /* 1 / 2 / 1 columns like the desktop mock: middle lane is wider */
  grid-template-columns: minmax(260px, 1fr) minmax(520px, 2fr) minmax(260px, 1fr);
  grid-auto-rows: minmax(300px, auto);
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(260px, auto);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
`;

const REQUIRED_DEFAULT_MODULES = [
  { id: 'ticketCount', type: 'ticketCount', position: { row: 2, col: 0 }, size: { w: 1, h: 1 } },
  { id: 'team', type: 'team', position: { row: 2, col: 1 }, size: { w: 1, h: 1 } },
  { id: 'analytics', type: 'analytics', position: { row: 2, col: 2 }, size: { w: 1, h: 1 } },
  { id: 'works', type: 'works', position: { row: 3, col: 0 }, size: { w: 1, h: 1 } },
];

export default function DesktopDashboard() {
  const dispatch = useDispatch();
  const { layout, editMode, newModuleDropdownOpen } = useSelector((state) => state.dashboard);
  
  const displayLayout = useMemo(() => {
    const base = Array.isArray(layout) ? [...layout] : [];
    const existingIds = new Set(base.map((m) => m?.id).filter(Boolean));
    for (const m of REQUIRED_DEFAULT_MODULES) {
      if (!existingIds.has(m.id)) {
        base.push(m);
      }
    }
    return base;
  }, [layout]);

  const sortedLayout = useMemo(() => {
    return [...displayLayout].sort((a, b) => {
      const ar = a?.position?.row ?? 0;
      const br = b?.position?.row ?? 0;
      const ac = a?.position?.col ?? 0;
      const bc = b?.position?.col ?? 0;
      if (ar !== br) return ar - br;
      return ac - bc;
    });
  }, [displayLayout]);

  useEffect(() => {
    // redux-persist can rehydrate after mount and overwrite our initial state.
    // Ensure the required default modules exist whenever layout changes.
    const requiredIds = ['ticketCount', 'team', 'analytics', 'works'];
    const existingIds = new Set((layout || []).map((m) => m?.id).filter(Boolean));
    const missing = requiredIds.some((id) => !existingIds.has(id));
    if (missing) {
      dispatch(ensureDefaultModules());
    }
  }, [dispatch, layout]);
  
  const handleAddModule = (type) => {
    dispatch(addModule({ type }));
    dispatch(setNewModuleDropdownOpen(false));
  };

  const toggleDropdown = () => {
    dispatch(setNewModuleDropdownOpen(!newModuleDropdownOpen));
  };

  const closeDropdown = () => {
    dispatch(setNewModuleDropdownOpen(false));
  };

  return (
    <DashboardContainer>
      <DashboardHeader>
        <div>
          <EditToggle>
            Edit dashboard -- (Toggles a drag and drop view where you can move the boxes around) --This would show →
          </EditToggle>
          <MetaRow>Modules on dashboard: {sortedLayout.length}</MetaRow>
        </div>
        
        <ActionButtons>
          <SecondaryButton onClick={() => dispatch(resetLayout())}>
            Reset dashboard
          </SecondaryButton>
          <AddButton onClick={toggleDropdown}>
            Add
          </AddButton>
          
          <DropdownContainer>
            <button
              onClick={toggleDropdown}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '8px 12px',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              New module <DropdownArrow>▼</DropdownArrow>
            </button>
            
            {newModuleDropdownOpen && (
              <>
                <Overlay onClick={closeDropdown} />
                <NewModuleDropdown>
                  {Object.entries(MODULE_TYPES).map(([key, value]) => (
                    <DropdownItem
                      key={key}
                      onClick={() => handleAddModule(key)}
                    >
                      {value.label}
                    </DropdownItem>
                  ))}
                </NewModuleDropdown>
              </>
            )}
          </DropdownContainer>
        </ActionButtons>
      </DashboardHeader>

      <ModulesGrid>
        {sortedLayout.map((module) => (
          <DashboardModule
            key={module.id}
            module={module}
            editMode={editMode}
          />
        ))}
      </ModulesGrid>
    </DashboardContainer>
  );
}

